import { createContext, ReactNode, useContext, useMemo, useState } from 'react';

import { UserMessage } from 'domains/change-cadastral.d';

const errorMessage = 'setContext function must be overridden';

const initialState = {
  steps: {
    start: false,
    validate: false,
    confirmation: false,
    userData: null,
    modalForm: false,
    success: false,
    error: null,
  },

  handleStart: (): void => {
    throw new Error(errorMessage);
  },
  handleValidate: (): void => {
    throw new Error(errorMessage);
  },
  handleConfirmation: (): void => {
    throw new Error(errorMessage);
  },
  handleForm: (): void => {
    throw new Error(errorMessage);
  },
  handleModalForm: (): void => {
    throw new Error(errorMessage);
  },
  handleError: (error: any) => {
    throw new Error(errorMessage);
  },
  handleSuccess: (): void => {
    throw new Error(errorMessage);
  },
  handleCorrectData: (): void => {
    throw new Error(errorMessage);
  },
};

export type ErrorRegistration = {
  errorId: string;
  detail: string;
  errorTitle: string;
  updateRegistrationError: any;
  isGenericError: boolean | undefined;
  isBusinessError: boolean;
  errorMessage: string;
  userMessage?: UserMessage;
};

type Address = {
  hasNoNumber: boolean;
  zipcode: string;
  address: string;
  number?: string;
  complement: string;
  district: string;
  state: string;
  city: string;
};

type InitialState = {
  steps: {
    start: boolean;
    validate: boolean;
    confirmation: boolean;
    userData: Address | null;
    modalForm: boolean;
    success: boolean;
    error: ErrorRegistration | null;
  };
};

type RegistrationControllerContextType = {
  handleStart: () => void;
  handleValidate: () => void;
  handleConfirmation: () => void;
  handleForm: (userData: Address | boolean) => void;
  handleModalForm: () => void;
  handleError: (error: ErrorRegistration | {}) => void;
  handleSuccess: () => void;
  handleCorrectData: () => void;
};

export const RegistrationControllerContext = createContext<
  InitialState & RegistrationControllerContextType
>(initialState);

export const useRegistrationControllerContext = () => useContext(RegistrationControllerContext);

export const RegistrationControllerContextProvider = ({ children }: { children: ReactNode }) => {
  const [steps, setSteps] = useState({ ...initialState.steps });

  const handleStart = () => {
    setSteps({ ...initialState.steps, start: true });
  };

  const handleValidate = () => {
    setSteps({ ...steps, validate: true });
  };

  const handleConfirmation = () => {
    setSteps({ ...steps, confirmation: true });
  };

  const handleForm = (userData: any = {}) => {
    setSteps({ ...steps, success: false, userData: { ...userData } });
  };

  const handleSuccess = () => {
    setSteps({ ...steps, success: true, error: null });
  };

  const handleError = (error: any = {}) => {
    setSteps({ ...steps, success: false, error: { ...error } });
  };

  const handleModalForm = () => {
    setSteps({ ...steps, modalForm: true });
  };

  const handleCorrectData = () => {
    setSteps({ ...steps, userData: null, success: false, error: null });
  };

  const stepsMemoied = useMemo(
    () => ({
      steps,
      handleStart,
      handleValidate,
      handleConfirmation,
      handleForm,
      handleModalForm,
      handleError,
      handleSuccess,
      handleCorrectData,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [steps]
  );

  return (
    <RegistrationControllerContext.Provider value={stepsMemoied}>
      {children}
    </RegistrationControllerContext.Provider>
  );
};
